import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../components/NotificationManager';
import { scheduledOrdersApi } from '../utils/advancedOrdersApi';
import { useUniversities } from '../context/universitiesContext';
import axios from 'axios';

const CreateScheduledOrder = () => {
  const navigate = useNavigate();
  const { universities } = useUniversities();
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [meals, setMeals] = useState([]);
  const [userUniversity, setUserUniversity] = useState(null);
  // Removed universityLandmarks state
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [recipientSearchResults, setRecipientSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [useMultipleSchedules, setUseMultipleSchedules] = useState(false);
  const [orderingForSomeoneElse, setOrderingForSomeoneElse] = useState(false);

  const [orderData, setOrderData] = useState({
    // Legacy fields for backward compatibility
    scheduledFor: '',
    repeatType: 'none',
    repeatDays: [],
    endDate: '',
    restaurant: '',
    meals: [],
    // New structure for multiple schedules
    scheduledTimes: [],
    recipient: {
      name: '',
      phoneNumber: '', // Keeping in state but not displaying in UI
      location: '', // Keeping in state but not displaying in UI
      nearestLandmark: '', // Keeping in state but not displaying in UI
      user: null // For ordering for other registered users
    },
    note: ''
  });

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadRestaurants();
    loadUserData();
  }, []);

  // Effect to handle orderingForSomeoneElse state when recipient changes
  useEffect(() => {
    // When a recipient user is selected, set orderingForSomeoneElse to true to show the correct UI state
    if (orderData.recipient.user) {
      setOrderingForSomeoneElse(true);
    }
  }, [orderData.recipient.user]);

  // Effect to update name when switching between Myself and Someone Else
  useEffect(() => {
    // If not ordering for someone else, reset to current user
    if (!orderingForSomeoneElse) {
      updateOrderData('recipient.user', null);
      // Set recipient name to current user's name
      if (currentUser?.username) {
        updateOrderData('recipient.name', currentUser.username);
      }
    }
  }, [orderingForSomeoneElse, currentUser]);

  // Update all schedules to have a restaurant when one is selected
  useEffect(() => {
    if (orderData.restaurant && useMultipleSchedules && orderData.scheduledTimes && orderData.scheduledTimes.length > 0) {
      // Make sure each schedule has the selected restaurant
      setOrderData(prev => {
        return {
          ...prev,
          scheduledTimes: prev.scheduledTimes.map(schedule => ({
            ...schedule,
            restaurant: orderData.restaurant
          }))
        };
      });
      
      // Also load meals for each schedule to select from
      if (selectedRestaurant) {
        loadMealsFromRestaurant(selectedRestaurant);
      }
    }
  }, [orderData.restaurant, useMultipleSchedules, selectedRestaurant]);

  const loadUserData = () => {
    const userData = localStorage.getItem('byteUser');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        const universityId = parsedUser?.university?._id;

        setCurrentUser(parsedUser);

        if (universityId) {
          setUserUniversity(parsedUser.university);
          // Landmarks fetch removed
        }

        // Auto-populate recipient data with user's data
        updateOrderData('recipient.name', parsedUser.username || '');
        // Phone number, location, landmark auto-population removed
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  };

  const loadRestaurants = async () => {
    try {
      const response = await scheduledOrdersApi.getRestaurantsForScheduling();
      if (response.success) {
        const restaurantData = response.data || [];
        setRestaurants(restaurantData);
      } else {
        showErrorNotification('Failed to load restaurants');
      }
    } catch (error) {
      console.error('Error loading restaurants:', error);
      showErrorNotification('Failed to load restaurants');
    }
  };

  const updateOrderData = (field, value) => {
    setOrderData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30);
    return now.toISOString().slice(0, 16);
  };

  const formatDateTimeForAPI = (dateTimeLocal) => {
    if (!dateTimeLocal) return '';
    const date = new Date(dateTimeLocal);
    return date.toISOString();
  };

  const formatEndDateForAPI = (dateLocal) => {
    if (!dateLocal) return '';
    const date = new Date(dateLocal + 'T23:59:59');
    return date.toISOString();
  };

  const validateOrderData = () => {
    const errors = [];

    // Validate restaurant selection (only for final submit, not for step validation)
    if (step > 2 && !orderData.restaurant) {
      errors.push('Please select a restaurant');
    }

    // Validate meals selection (only for final submit, not for step validation)
    if (step > 3 && !useMultipleSchedules && (!orderData.meals || orderData.meals.length === 0)) {
      errors.push('Please select at least one meal');
    }

    // Make sure all dates are at least 30 minutes in the future
    const minDate = new Date();
    minDate.setMinutes(minDate.getMinutes() + 30);

    // Validate scheduling data - different validation based on mode
    if (useMultipleSchedules && orderData.scheduledTimes) {
      // Validate multiple schedules
      if (orderData.scheduledTimes.length === 0) {
        errors.push('Please add at least one schedule');
        return errors;
      }

      // Validate each schedule
      orderData.scheduledTimes.forEach((schedule, index) => {
        if (!schedule.scheduledFor) {
          errors.push(`Schedule #${index + 1}: Please select a date and time`);
        } else {
          const scheduledTime = new Date(schedule.scheduledFor);
          if (scheduledTime < minDate) {
            errors.push(`Schedule #${index + 1}: Scheduled time must be at least 30 minutes in the future`);
          }
        }

        // Validate repeat logic
        if (schedule.repeatType === 'daily' && schedule.repeatDays?.length > 0) {
          errors.push(`Schedule #${index + 1}: Daily repeat type should not have specific days selected`);
        }

        if (schedule.repeatType === 'weekly' && (!schedule.repeatDays || schedule.repeatDays.length === 0)) {
          errors.push(`Schedule #${index + 1}: Weekly repeat type requires specific days to be selected`);
        }

        // Validate meals for this specific schedule - now checking in step 3 as well
        if (step === 3 && (!schedule.meals || schedule.meals.length === 0)) {
          errors.push(`Schedule #${index + 1}: Please select at least one meal`);
        }
        else if (step > 3 && (!schedule.meals || schedule.meals.length === 0)) {
          errors.push(`Schedule #${index + 1}: Please select at least one meal`);
        }
      });
    } else {
      // Validate single schedule
      if (!orderData.scheduledFor) {
        errors.push('Please select a date and time');
      } else {
        const orderDate = new Date(orderData.scheduledFor);
        if (orderDate < minDate) {
          errors.push('Scheduled time must be at least 30 minutes in the future');
        }
      }

      // Validate repeat logic
      if (orderData.repeatType === 'daily' && orderData.repeatDays?.length > 0) {
        errors.push('Daily repeat type should not have specific days selected');
      }

      if (orderData.repeatType === 'weekly' && (!orderData.repeatDays || orderData.repeatDays.length === 0)) {
        errors.push('Weekly repeat type requires specific days to be selected');
      }
      
      // Validate meals for simple scheduling (in step 3)
      if (step === 3 && (!orderData.meals || orderData.meals.length === 0)) {
        errors.push('Please select at least one meal');
      }
    }

    return errors;
  };

  const generatePreview = async () => {
    if (useMultipleSchedules) {
      return generateMultiSchedulePreview();
    }
    
    const errors = validateOrderData();
    if (errors.length > 0) {
      showErrorNotification('Please fix the following errors:', { details: errors.join(', ') });
      return;
    }

    setPreviewLoading(true);
    try {
      const previewData = {
        ...orderData,
        scheduledFor: formatDateTimeForAPI(orderData.scheduledFor),
        endDate: orderData.endDate ? formatEndDateForAPI(orderData.endDate) : undefined
      };

      const response = await scheduledOrdersApi.previewScheduledOrder(previewData);
      if (response.success) {
        setPreview(response.data);
      } else {
        showErrorNotification('Failed to generate preview', { details: response.message });
        setPreview(null);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      showErrorNotification('Failed to generate preview');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadMealsFromRestaurant = (restaurant) => {
    // Check for menu in different possible field names
    const menu = restaurant?.menu || restaurant?.meals || [];

    if (menu && menu.length > 0) {
      setMeals(menu);
    } else {
      setMeals([]);
      showWarningNotification('No menu available for this restaurant');
    }
  };

  const getRestaurantId = (restaurant) => {
    return restaurant._id || restaurant.id;
  };

  const handleRestaurantSelect = (restaurant) => {
    const restaurantId = getRestaurantId(restaurant);
    if (!restaurantId) {
      showErrorNotification('Invalid restaurant selection');
      return;
    }
    setSelectedRestaurant(restaurant);
    updateOrderData('restaurant', restaurantId);
    loadMealsFromRestaurant(restaurant);
    setStep(3);
  };

  const toggleMeal = (meal) => {
    const currentMeals = orderData.meals || [];
    const existingMealIndex = currentMeals.findIndex(m => m.meal === meal._id);

    let updatedMeals;
    if (existingMealIndex >= 0) {
      // Remove meal
      updatedMeals = currentMeals.filter((_, index) => index !== existingMealIndex);
    } else {
      // Add meal with quantity 1
      updatedMeals = [...currentMeals, { meal: meal._id, quantity: 1 }];
    }

    updateOrderData('meals', updatedMeals);
  };

  const updateMealQuantity = (mealId, quantity) => {
    const updatedMeals = orderData.meals.map(m =>
      m.meal === mealId ? { ...m, quantity: Math.max(1, quantity) } : m
    );
    updateOrderData('meals', updatedMeals);
  };
  
  // New function to search for recipients in the same university
  const searchRecipients = async (query) => {
    if (!query || query.length < 2) {
      setRecipientSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await scheduledOrdersApi.searchRecipientsInUniversity(query);
      if (response.success && response.data) {
        setRecipientSearchResults(response.data);
      } else {
        setRecipientSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching for recipients:', error);
      setRecipientSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle recipient selection
  const handleRecipientSelect = (recipient) => {
    if (!recipient || !recipient._id) {
      console.error('Invalid recipient selected:', recipient);
      return;
    }
    
    updateOrderData('recipient.user', recipient._id);
    updateOrderData('recipient.name', recipient.username || '');
    // We keep other recipient fields empty since they'll be retrieved from the selected user
    setSearchQuery('');
    setRecipientSearchResults([]);
    setOrderingForSomeoneElse(true); // Keep "Someone Else" mode active after selection
  };
  
  // Add a new schedule to scheduledTimes array
  const addSchedule = () => {
    // Generate a default time at least 30 minutes in the future
    const defaultTime = new Date();
    defaultTime.setMinutes(defaultTime.getMinutes() + 30);
    const formattedDefaultTime = defaultTime.toISOString().slice(0, 16);
    
    setOrderData(prev => {
      const newSchedule = {
        scheduledFor: formattedDefaultTime,
        repeatType: prev.repeatType || 'none',
        repeatDays: [...(prev.repeatDays || [])],
        dayTimeSchedule: [],
        endDate: prev.endDate || '',
        meals: [] // Empty meals array - user will select meals specifically for this schedule
      };
      
      return {
        ...prev,
        scheduledTimes: [...(prev.scheduledTimes || []), newSchedule]
      };
    });
  };
  
  // Update a specific schedule in scheduledTimes array
  const updateSchedule = (index, field, value) => {
    setOrderData(prev => {
      const updatedSchedules = [...(prev.scheduledTimes || [])];
      
      if (!updatedSchedules[index]) {
        return prev;
      }
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedSchedules[index] = {
          ...updatedSchedules[index],
          [parent]: {
            ...(updatedSchedules[index][parent] || {}),
            [child]: value
          }
        };
      } else {
        updatedSchedules[index] = {
          ...updatedSchedules[index],
          [field]: value
        };
      }
      
      return {
        ...prev,
        scheduledTimes: updatedSchedules
      };
    });
  };
  
  // Remove a schedule from scheduledTimes array
  const removeSchedule = (index) => {
    setOrderData(prev => {
      const updatedSchedules = [...(prev.scheduledTimes || [])];
      updatedSchedules.splice(index, 1);
      
      return {
        ...prev,
        scheduledTimes: updatedSchedules
      };
    });
  };
  
  // Generate a more comprehensive preview that accounts for multiple schedules
  const generateMultiSchedulePreview = async () => {
    const errors = validateOrderData();
    if (errors.length > 0) {
      showErrorNotification('Please fix the following errors:', { details: errors.join(', ') });
      return;
    }

    setPreviewLoading(true);
    try {
      // Prepare the data, ensuring scheduledTimes is properly formatted
      const previewData = {
        ...orderData,
        restaurant: orderData.restaurant,
        // Format each schedule's dates
        scheduledTimes: orderData.scheduledTimes.map(schedule => ({
          ...schedule,
          scheduledFor: formatDateTimeForAPI(schedule.scheduledFor),
          endDate: schedule.endDate ? formatEndDateForAPI(schedule.endDate) : undefined
        }))
      };

      // For backward compatibility
      if (orderData.scheduledFor) {
        previewData.scheduledFor = formatDateTimeForAPI(orderData.scheduledFor);
        previewData.endDate = orderData.endDate ? formatEndDateForAPI(orderData.endDate) : undefined;
      }

      const response = await scheduledOrdersApi.previewScheduledOrder(previewData);
      if (response.success) {
        setPreview(response.data);
      } else {
        showErrorNotification('Failed to generate preview', { details: response.message });
        setPreview(null);
      }
    } catch (error) {
      console.error('Error generating multi-schedule preview:', error);
      showErrorNotification('Failed to generate preview');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateOrderData();
    if (errors.length > 0) {
      showErrorNotification('Please fix the following errors:', { details: errors.join(', ') });
      return;
    }

    setLoading(true);
    try {
      // Prepare the data for submission, supporting both legacy and new format
      const submitData = {
        ...orderData,
        restaurant: orderData.restaurant,
        note: orderData.note,
        recipient: orderData.recipient
      };
      
      // Format dates for legacy format (backward compatibility)
      if (orderData.scheduledFor) {
        submitData.scheduledFor = formatDateTimeForAPI(orderData.scheduledFor);
        submitData.endDate = orderData.endDate ? formatEndDateForAPI(orderData.endDate) : undefined;
      }
      
      // If using multiple schedules, format each schedule's dates
      if (useMultipleSchedules && orderData.scheduledTimes && orderData.scheduledTimes.length > 0) {
        submitData.scheduledTimes = orderData.scheduledTimes.map(schedule => ({
          ...schedule,
          scheduledFor: formatDateTimeForAPI(schedule.scheduledFor),
          endDate: schedule.endDate ? formatEndDateForAPI(schedule.endDate) : undefined
        }));
      }

      const response = await scheduledOrdersApi.createScheduledOrder(submitData);
      if (response.success) {
        showSuccessNotification('Scheduled order created successfully!', {
          details: `Order will execute ${response.executionDetails?.totalExecutionCount || preview?.summary?.totalExecutions || 1} time(s)`
        });
        navigate('/user/advanced-orders');
      } else {
        showErrorNotification('Failed to create scheduled order', { details: response.message });
      }
    } catch (error) {
      console.error('Error creating scheduled order:', error);
      showErrorNotification('Failed to create scheduled order');
    } finally {
      setLoading(false);
    }
  };

  const stepConfig = [
    { num: 1, label: 'Schedule', icon: CalendarIcon },
    { num: 2, label: 'Restaurant', icon: BuildingStorefrontIcon },
    { num: 3, label: 'Meals', icon: ShoppingBagIcon },
    { num: 4, label: 'Review', icon: ClockIcon }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-accentwhite mb-12">
      {/* Header */}
      <div className="mt-8 bg-white shadow-sm border-b border-secondary-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/user/advanced-orders')}
              className="p-2 rounded-xl hover:bg-secondary-50 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-olive-light" />
            </button>
            <div className="flex items-center gap-3">
              <SparklesIcon className="w-6 h-6 text-cheese" />
              <h1 className="text-xl md:text-2xl font-bold text-crust font-display">
                Create Scheduled Order
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white border-b border-secondary-200">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {stepConfig.map((s, index) => {
              const Icon = s.icon;
              const isActive = step >= s.num;
              const isCompleted = step > s.num;

              return (
                <div key={s.num} className="flex items-center flex-shrink-0 min-w-0">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 rounded-full flex items-center justify-center transition-all ${isCompleted ? 'bg-green-500 text-white' :
                      isActive ? 'bg-cheese text-crust' : 'bg-gray-200 text-gray-500'
                      }`}>
                      {isCompleted ? (
                        <span className="text-sm sm:text-base md:text-lg">✓</span>
                      ) : (
                        <Icon className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5" />
                      )}
                    </div>
                    <span className={`mt-1 sm:mt-2 text-xs sm:text-sm font-semibold text-center ${isActive ? 'text-crust' : 'text-gray-500'
                      }`}>
                      {s.label}
                    </span>
                  </div>
                  {index < stepConfig.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 sm:mx-3 md:mx-4 transition-all ${step > s.num ? 'bg-cheese' : 'bg-gray-200'
                      }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white rounded-2xl shadow-lg p-6 md:p-8"
        >
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-crust mb-2">When would you like your order?</h2>
                <p className="text-olive-light">Set up your delivery schedule</p>
              </div>

              <div className="space-y-6">
                {/* Only show simple scheduling when useMultipleSchedules is false */}
                {!useMultipleSchedules ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-crust mb-2">Date & Time</label>
                        <input
                          type="datetime-local"
                          value={orderData.scheduledFor}
                          onChange={(e) => updateOrderData('scheduledFor', e.target.value)}
                          min={getMinDateTime()}
                          className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                        />
                        {orderData.scheduledFor && (
                          <p className="mt-2 text-sm text-olive-light">
                            Your food will be delivered on {new Date(orderData.scheduledFor).toLocaleDateString()} at {new Date(orderData.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-crust mb-2">Repeat</label>
                        <select
                          value={orderData.repeatType}
                          onChange={(e) => {
                            updateOrderData('repeatType', e.target.value);
                            if (e.target.value === 'none' || e.target.value === 'daily') {
                              updateOrderData('repeatDays', []);
                            }
                          }}
                          className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                        >
                          <option value="none">One-time</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                        <p className="mt-2 text-sm text-olive-light">
                          {orderData.repeatType === 'none' ? 
                            'Your order will be delivered just once on the selected date and time.' : 
                            orderData.repeatType === 'daily' ? 
                              'Your order will be delivered every day at the same time until the end date (if set).' : 
                              'Your order will be delivered on selected days of the week until the end date (if set).'}
                        </p>
                      </div>
                    </div>

                    {orderData.repeatType === 'weekly' && (
                      <div>
                        <label className="block text-sm font-bold text-crust mb-2">Select Days</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const currentDays = orderData.repeatDays || [];
                                const updatedDays = currentDays.includes(day)
                                  ? currentDays.filter(d => d !== day)
                                  : [...currentDays, day];
                                updateOrderData('repeatDays', updatedDays);
                              }}
                              className={`p-3 rounded-xl font-semibold transition-all ${orderData.repeatDays?.includes(day)
                                ? 'bg-cheese text-crust shadow-lg'
                                : 'bg-secondary-100 text-olive-light hover:bg-secondary-200'
                                }`}
                            >
                              {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                            </button>
                          ))}
                        </div>
                        {orderData.repeatDays && orderData.repeatDays.length > 0 ? (
                          <p className="mt-3 text-sm text-olive-light">
                            Your order will be delivered every {orderData.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')} at {orderData.scheduledFor ? new Date(orderData.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'the selected time'}.
                          </p>
                        ) : (
                          <p className="mt-3 text-sm text-pepperoni">
                            Please select at least one day of the week for your recurring order.
                          </p>
                        )}
                      </div>
                    )}

                    {orderData.repeatType !== 'none' && (
                      <div>
                        <label className="block text-sm font-bold text-crust mb-2">End Date (Optional)</label>
                        <input
                          type="date"
                          value={orderData.endDate}
                          onChange={(e) => updateOrderData('endDate', e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                        />
                        {orderData.endDate ? (
                          <p className="mt-2 text-sm text-olive-light">
                            Your recurring order will end on {new Date(orderData.endDate).toLocaleDateString()}.
                          </p>
                        ) : (
                          <p className="mt-2 text-sm text-olive-light">
                            Without an end date, your order will continue indefinitely until canceled.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Delivery count estimation */}
                    {orderData.scheduledFor && orderData.repeatType !== 'none' && (
                      <div className="mt-4 p-4 bg-cheese/10 rounded-xl border border-cheese/20">
                        <h3 className="font-bold text-crust mb-2">Estimated Order Summary</h3>
                        {orderData.repeatType === 'daily' ? (
                          <p className="text-sm text-olive">
                            {orderData.endDate ? (
                              <>
                                This order will be delivered daily from {new Date(orderData.scheduledFor).toLocaleDateString()} to {new Date(orderData.endDate).toLocaleDateString()}, 
                                approximately {Math.max(1, Math.round((new Date(orderData.endDate) - new Date(orderData.scheduledFor)) / (24 * 60 * 60 * 1000)))} times.
                              </>
                            ) : (
                              <>
                                This order will be delivered daily starting {new Date(orderData.scheduledFor).toLocaleDateString()} until canceled.
                              </>
                            )}
                          </p>
                        ) : orderData.repeatType === 'weekly' && orderData.repeatDays?.length > 0 ? (
                          <p className="text-sm text-olive">
                            {orderData.endDate ? (
                              <>
                                This order will be delivered {orderData.repeatDays.length} time(s) per week on {orderData.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')},
                                from {new Date(orderData.scheduledFor).toLocaleDateString()} to {new Date(orderData.endDate).toLocaleDateString()}.
                              </>
                            ) : (
                              <>
                                This order will be delivered {orderData.repeatDays.length} time(s) per week on {orderData.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')} until canceled.
                              </>
                            )}
                          </p>
                        ) : (
                          <p className="text-sm text-olive">
                            Please complete your scheduling options to see an estimate.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* Display a message when in advanced mode */
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="font-bold text-blue-700">Using Advanced Scheduling</h4>
                    </div>
                    <p className="text-sm text-blue-600">
                      You're now using advanced scheduling. You can create multiple schedules with different delivery times and patterns. Each schedule can have its own delivery time, repeat pattern, and end date.
                    </p>
                  </div>
                )}
                
                {/* Advanced scheduling toggle */}
                <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-crust">Advanced Scheduling Options</label>
                    <button
                      type="button"
                      onClick={() => {
                        // When toggling to advanced mode, initialize the first schedule
                        if (!useMultipleSchedules) {
                          if (!orderData.scheduledTimes || orderData.scheduledTimes.length === 0) {
                            addSchedule();
                          }
                        }
                        setUseMultipleSchedules(!useMultipleSchedules);
                      }}
                      className={`text-sm font-bold px-3 py-1 rounded-full transition-all ${
                        useMultipleSchedules 
                          ? 'bg-gray-200 text-olive hover:bg-gray-300' 
                          : 'bg-cheese text-crust hover:bg-cheese/90'
                      }`}
                    >
                      {useMultipleSchedules ? 'Switch to Simple Scheduling' : 'Switch to Advanced Scheduling'}
                    </button>
                  </div>
                  <p className="text-xs text-olive-light">
                    Advanced scheduling allows you to set up multiple schedules with different times, days, and meal selections.
                  </p>
                  <div className="mt-2 text-xs bg-cheese/10 p-2 rounded-lg">
                    <span className="font-semibold">Example use case:</span> Order breakfast daily at 8am, lunch on weekdays at 1pm, and dinner on weekends at 7pm.
                  </div>
                </div>
                
                {/* Multiple schedules UI */}
                {useMultipleSchedules && (
                  <div className="mt-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold text-crust">Multiple Schedules</h3>
                      <button
                        type="button"
                        onClick={addSchedule}
                        className="bg-cheese text-crust px-4 py-2 rounded-xl font-bold text-sm hover:bg-cheese/90 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Add Schedule
                      </button>
                    </div>
                    
                    {orderData.scheduledTimes && orderData.scheduledTimes.length > 0 ? (
                      <div className="space-y-6">
                        {orderData.scheduledTimes.map((schedule, index) => (
                          <div key={index} className="border border-secondary-200 rounded-xl p-4 bg-white">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-bold text-crust">Schedule #{index + 1}</h4>
                              {orderData.scheduledTimes.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeSchedule(index)}
                                  className="text-red-500 hover:text-red-700 font-bold text-sm"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-bold text-crust mb-2">Date & Time</label>
                                <input
                                  type="datetime-local"
                                  value={schedule.scheduledFor || ''}
                                  onChange={(e) => updateSchedule(index, 'scheduledFor', e.target.value)}
                                  min={getMinDateTime()}
                                  className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                                />
                                {schedule.scheduledFor && (
                                  <p className="mt-2 text-xs text-olive-light">
                                    First delivery: {new Date(schedule.scheduledFor).toLocaleDateString()} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </p>
                                )}
                              </div>
                              
                              <div>
                                <label className="block text-sm font-bold text-crust mb-2">Repeat</label>
                                <div className="relative">
                                  <select
                                    value={schedule.repeatType || 'none'}
                                    onChange={(e) => {
                                      updateSchedule(index, 'repeatType', e.target.value);
                                      if (e.target.value === 'none' || e.target.value === 'daily') {
                                        updateSchedule(index, 'repeatDays', []);
                                      }
                                    }}
                                    className="w-full p-4 pr-10 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans appearance-none bg-white"
                                  >
                                    <option value="none">One-time</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                  </select>
                                  <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                                    <svg className="w-5 h-5 text-olive-light" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                    </svg>
                                  </div>
                                </div>
                                <p className="mt-2 text-xs text-olive-light">
                                  {schedule.repeatType === 'none' ? 
                                    'One-time delivery' : 
                                    schedule.repeatType === 'daily' ? 
                                      'Delivers every day' : 
                                      'Delivers on selected days'}
                                </p>
                              </div>
                            </div>
                            
                            {schedule.repeatType === 'weekly' && (
                              <div className="mb-4">
                                <label className="block text-sm font-bold text-crust mb-3">Select Days</label>
                                <div className="grid grid-cols-7 gap-2">
                                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                                    const isSelected = (schedule.repeatDays || []).includes(day);
                                    const dayLetter = day.charAt(0).toUpperCase();
                                    
                                    return (
                                      <button
                                        key={day}
                                        type="button"
                                        onClick={() => {
                                          const currentDays = schedule.repeatDays || [];
                                          const updatedDays = currentDays.includes(day)
                                            ? currentDays.filter(d => d !== day)
                                            : [...currentDays, day];
                                          updateSchedule(index, 'repeatDays', updatedDays);
                                        }}
                                        className={`
                                          h-12 w-12 rounded-full font-bold transition-all duration-200
                                          flex items-center justify-center text-sm
                                          ${isSelected 
                                            ? 'bg-cheese text-crust shadow-md transform scale-110' 
                                            : 'bg-secondary-100 text-olive-light hover:bg-secondary-200 hover:shadow-sm'
                                          }
                                        `}
                                        aria-label={day}
                                        title={day.charAt(0).toUpperCase() + day.slice(1)}
                                      >
                                        {dayLetter}
                                      </button>
                                    );
                                  })}
                                </div>
                                
                                {(schedule.repeatDays || []).length === 0 && schedule.repeatType === 'weekly' ? (
                                  <p className="text-xs text-pepperoni mt-2">Please select at least one day of the week</p>
                                ) : (schedule.repeatDays || []).length > 0 && (
                                  <p className="text-xs text-olive-light mt-2">
                                    Delivering on: {schedule.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {schedule.repeatType !== 'none' && (
                              <div className="mb-4">
                                <label className="block text-sm font-bold text-crust mb-2">End Date (Optional)</label>
                                <input
                                  type="date"
                                  value={schedule.endDate || ''}
                                  onChange={(e) => updateSchedule(index, 'endDate', e.target.value)}
                                  min={new Date().toISOString().split('T')[0]}
                                  className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans"
                                />
                                {schedule.endDate ? (
                                  <p className="mt-2 text-xs text-olive-light">
                                    Recurring delivery ends: {new Date(schedule.endDate).toLocaleDateString()}
                                  </p>
                                ) : (
                                  <p className="mt-2 text-xs text-olive-light">
                                    Delivery will continue until you cancel
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Schedule summary */}
                            <div className="p-3 bg-cheese/10 rounded-lg border border-cheese/20 mb-4">
                              <h5 className="font-semibold text-sm text-crust mb-1">Schedule Summary</h5>
                              <p className="text-xs text-olive">
                                {schedule.scheduledFor ? (
                                  schedule.repeatType === 'none' ? (
                                    <>One-time delivery on {new Date(schedule.scheduledFor).toLocaleDateString()} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                  ) : schedule.repeatType === 'daily' ? (
                                    schedule.endDate ? (
                                      <>Daily delivery at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} from {new Date(schedule.scheduledFor).toLocaleDateString()} to {new Date(schedule.endDate).toLocaleDateString()}</>
                                    ) : (
                                      <>Daily delivery at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} starting {new Date(schedule.scheduledFor).toLocaleDateString()} until canceled</>
                                    )
                                  ) : (schedule.repeatDays || []).length > 0 ? (
                                    schedule.endDate ? (
                                      <>Weekly delivery on {schedule.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} from {new Date(schedule.scheduledFor).toLocaleDateString()} to {new Date(schedule.endDate).toLocaleDateString()}</>
                                    ) : (
                                      <>Weekly delivery on {schedule.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} starting {new Date(schedule.scheduledFor).toLocaleDateString()} until canceled</>
                                    )
                                  ) : (
                                    <>Please select days for your weekly delivery</>
                                  )
                                ) : (
                                  <>Please select a start date and time</>
                                )}
                              </p>
                            </div>
                            
                            {/* Notice about meal selection */}
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                              <div className="flex items-start gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div>
                                  <h5 className="font-bold text-blue-700 text-sm">About Meal Selection</h5>
                                  <p className="text-xs text-blue-600 mt-1">
                                    Complete all steps first (select a restaurant and meals), then in the final Review step, you'll be able to customize meals for each schedule.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-8 bg-secondary-50 border-2 border-dashed border-secondary-200 rounded-xl">
                        <div className="flex justify-center mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-secondary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <p className="text-olive-light mb-4 font-medium">No schedules added yet.</p>
                        <button
                          type="button"
                          onClick={addSchedule}
                          className="bg-cheese text-crust px-6 py-3 rounded-xl font-bold hover:bg-cheese/90 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 mx-auto"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Add Your First Schedule
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-crust">Delivery Information</h3>
                  
                  <div className="bg-cheese/10 border border-cheese rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cheese" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-crust">
                        Your scheduled order will be delivered to your registered address.
                      </p>
                    </div>
                  </div>

                  {/* Order Recipient Selector - Improved Design */}
                  <div className="bg-secondary-50 rounded-xl p-6 mb-4 shadow-soft">
                    <h4 className="font-bold text-crust mb-4">Who is this order for?</h4>
                    
                    {/* Toggle Switch Style Selector */}
                    <div className="flex items-center justify-center w-full mb-6">
                      <div className="bg-secondary-100 rounded-full p-1 flex w-full max-w-xs">
                        <button
                          type="button"
                          onClick={() => {
                            updateOrderData('recipient.user', null);
                            // Set recipient name to current user's name when switching to "Myself"
                            if (currentUser?.username) {
                              updateOrderData('recipient.name', currentUser.username);
                            }
                            // Clear search results when switching to "Myself"
                            setSearchQuery('');
                            setRecipientSearchResults([]);
                            setOrderingForSomeoneElse(false); // Turn off Someone Else mode
                          }}
                          className={`w-1/2 py-3 px-4 rounded-full text-center transition-all duration-200 ${
                            !orderingForSomeoneElse
                              ? 'bg-cheese text-crust font-bold shadow-md transform scale-105' 
                              : 'text-olive-light hover:bg-secondary-200'
                          }`}
                        >
                          Myself
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            // Just set a temporary flag to indicate we want to search for someone else
                            // We'll set the actual recipient.user when they select someone
                            setOrderingForSomeoneElse(true);
                            setSearchQuery('');
                            setRecipientSearchResults([]);
                          }}
                          className={`w-1/2 py-3 px-4 rounded-full text-center transition-all duration-200 ${
                            orderingForSomeoneElse
                              ? 'bg-cheese text-crust font-bold shadow-md transform scale-105' 
                              : 'text-olive-light hover:bg-secondary-200'
                          }`}
                        >
                          Someone Else
                        </button>
                      </div>
                    </div>
                    
                    {/* Selected Recipient or Self Display */}
                    {orderData.recipient.user ? (
                      <div className="bg-white rounded-xl border border-secondary-200 p-4 mb-4 shadow-soft">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-cheese rounded-full flex items-center justify-center text-white text-lg font-bold shadow-md">
                              {orderData.recipient.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-bold text-crust text-lg">{orderData.recipient.name}</p>
                              <div className="flex items-center gap-1 text-olive-light text-sm mt-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                <span>Order will use their delivery information</span>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              updateOrderData('recipient.user', null);
                              updateOrderData('recipient.name', ''); // Clear recipient name
                              loadUserData(); // Reset to current user's data
                              setOrderingForSomeoneElse(true); // Set flag to show search bar again
                            }}
                            className="flex items-center gap-1 text-pepperoni hover:text-primary-700 font-medium text-sm transition-colors px-3 py-2 rounded-lg hover:bg-primary-50"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Change
                          </button>
                        </div>
                      </div>
                    ) : !orderingForSomeoneElse ? (
                      /* Only show current user when "Myself" is selected */
                      <div className="bg-white rounded-xl border border-secondary-200 p-4 mb-4 shadow-soft">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-cheese rounded-full flex items-center justify-center text-white text-lg font-bold shadow-md">
                            {currentUser?.username?.charAt(0).toUpperCase() || 'Y'}
                          </div>
                          <div>
                            <p className="font-bold text-crust text-lg">{currentUser?.username || 'You'}</p>
                            <div className="flex items-center gap-1 text-olive-light text-sm mt-1">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Your delivery information will be used</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Empty state message when "Someone Else" is selected but no one has been chosen yet */
                      <div className="bg-white rounded-xl border border-secondary-200 p-4 mb-4 shadow-soft">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-olive text-lg font-bold">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-bold text-crust text-lg">Select a recipient</p>
                            <div className="flex items-center gap-1 text-olive-light text-sm mt-1">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <span>Search for someone in your university</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Recipient search - Only show when "Someone Else" is selected and no recipient is chosen yet */}
                    {orderingForSomeoneElse && !orderData.recipient.user && (
                      <div className="mt-6 transition-all duration-300">
                        <div className="flex items-center gap-2 mb-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cheese" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <label className="font-bold text-crust">
                            Find someone in your university
                          </label>
                        </div>

                        <div className="relative">
                          <div className="flex items-center relative">
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                searchRecipients(e.target.value);
                              }}
                              placeholder="Enter name or username..."
                              className="w-full p-4 pl-12 border border-secondary-300 bg-white rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans shadow-sm"
                            />
                            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-olive-light">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            {isSearching && (
                              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cheese"></div>
                              </div>
                            )}
                          </div>
                          
                          {/* Search results */}
                          {recipientSearchResults.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-card border border-secondary-200 max-h-72 overflow-y-auto">
                              <div className="p-2 border-b border-secondary-100">
                                <p className="text-xs text-olive-light font-medium">
                                  {recipientSearchResults.length} {recipientSearchResults.length === 1 ? 'person' : 'people'} found
                                </p>
                              </div>
                              {recipientSearchResults.map(recipient => (
                                <div
                                  key={recipient._id}
                                  onClick={() => handleRecipientSelect(recipient)}
                                  className="p-3 hover:bg-secondary-50 cursor-pointer border-b border-secondary-100 last:border-b-0 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-cheese rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                                      {recipient.username?.charAt(0).toUpperCase() || '?'}
                                    </div>
                                    <div>
                                      <p className="font-bold text-crust">{recipient.username}</p>
                                      <p className="text-xs text-olive-light">{recipient.email}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Delivery Information Display */}
                  <div className="space-y-4">
                    {userUniversity && (
                      <div className="bg-secondary-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-olive-light">
                          <BuildingStorefrontIcon className="w-4 h-4" />
                          <span>University: <span className="font-semibold text-crust">{userUniversity.name}</span></span>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-crust mb-2">Your Name</label>
                        <div className="w-full p-4 border border-secondary-100 bg-secondary-50 rounded-xl font-sans">
                          {orderData.recipient.name || 'Not set'}
                        </div>
                      </div>
                      </div>

                    <div className="mt-4">
                      <label className="block text-sm font-bold text-crust mb-2">Special Instructions (Optional)</label>
                      <textarea
                        value={orderData.note}
                        onChange={(e) => updateOrderData('note', e.target.value)}
                        placeholder="Any special delivery instructions..."
                        rows={3}
                        className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  disabled={
                    // Different validation for simple vs advanced mode
                    useMultipleSchedules 
                      ? (!orderData.scheduledTimes || orderData.scheduledTimes.length === 0 || !orderData.scheduledTimes.some(s => s.scheduledFor)) 
                      : (!orderData.scheduledFor ||
                        (orderData.repeatType === 'weekly' && (!orderData.repeatDays || orderData.repeatDays.length === 0)))
                  }
                  className="px-8 py-3 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Choose Restaurant
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-crust mb-2">Choose a Restaurant</h2>
                <p className="text-olive-light">Select where you'd like to order from</p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {restaurants.map((restaurant) => (
                  <motion.div
                    key={getRestaurantId(restaurant)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleRestaurantSelect(restaurant)}
                    className="p-6 border border-secondary-200 rounded-xl hover:border-cheese hover:shadow-lg transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                        {restaurant.image ? (
                          <img
                            src={restaurant.image}
                            alt={restaurant.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-full h-full bg-gradient-to-br from-cheese to-primary-600 flex items-center justify-center ${restaurant.image ? 'hidden' : 'flex'}`}
                        >
                          <BuildingStorefrontIcon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-crust">{restaurant.name}</h3>
                        <p className="text-olive-light text-sm">{restaurant.cuisine}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-sm text-green-600 font-semibold">
                            ⭐ {restaurant.rating || '4.5'}
                          </span>
                          <span className="text-sm text-olive-light">
                            {restaurant.deliveryTime || '30-45'} min
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-all"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-crust mb-2">Select Your Meals</h2>
                <p className="text-olive-light">Choose what you'd like to order</p>
              </div>

              {/* Note about meal selection for multiple schedules */}
              {useMultipleSchedules && (
                <div className="bg-gradient-to-r from-cheese/20 to-secondary-50 border border-cheese/30 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <InformationCircleIcon className="w-6 h-6 text-cheese flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-crust">Advanced Scheduling Mode</h3>
                      <p className="text-olive">
                        You're using advanced scheduling with multiple deliveries. Please select meals for each schedule below.
                        Each schedule can have different meals.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!useMultipleSchedules ? (
                // Regular single schedule meal selection
                <div className="border border-secondary-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-secondary-50">
                      <tr>
                        <th className="text-left py-3 px-4 font-bold text-crust">Meal</th>
                        <th className="text-right py-3 px-4 font-bold text-crust">Price</th>
                        <th className="text-center py-3 px-4 font-bold text-crust">Quantity</th>
                        <th className="text-right py-3 px-4 font-bold text-crust">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {meals.map((meal) => {
                        const selectedMeal = orderData.meals?.find(m => m.meal === meal._id);
                        const isSelected = !!selectedMeal;

                        return (
                          <tr 
                            key={meal._id}
                            className={`border-b border-secondary-100 hover:bg-secondary-50 transition-colors ${
                              isSelected ? 'bg-cheese/10' : ''
                            }`}
                          >
                            <td className="py-3 px-4">
                              <div>
                                <h3 className="font-bold text-crust">{meal.name}</h3>
                                <p className="text-olive-light text-sm line-clamp-1">{meal.description}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-pepperoni">
                              ₦{meal.price}
                            </td>
                            <td className="py-3 px-4">
                              {isSelected ? (
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => updateMealQuantity(meal._id, selectedMeal.quantity - 1)}
                                    className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center font-bold text-olive-light hover:bg-secondary-300"
                                  >
                                    -
                                  </button>
                                  <span className="font-bold text-crust min-w-[2rem] text-center">
                                    {selectedMeal.quantity}
                                  </span>
                                  <button
                                    onClick={() => updateMealQuantity(meal._id, selectedMeal.quantity + 1)}
                                    className="w-8 h-8 rounded-full bg-cheese flex items-center justify-center font-bold text-crust hover:bg-cheese/80"
                                  >
                                    +
                                  </button>
                                </div>
                              ) : (
                                <div className="text-center text-gray-400">—</div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right">
                              {isSelected ? (
                                <button
                                  onClick={() => toggleMeal(meal)}
                                  className="inline-flex items-center px-3 py-1 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors"
                                >
                                  Remove
                                </button>
                              ) : (
                                <button
                                  onClick={() => toggleMeal(meal)}
                                  className="inline-flex items-center px-3 py-1 bg-pepperoni text-white rounded-lg font-medium hover:bg-primary-800 transition-colors"
                                >
                                  Add
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Advanced scheduling - show meal selection per schedule
                <div className="space-y-8">
                  {orderData.scheduledTimes && orderData.scheduledTimes.length > 0 ? (
                    orderData.scheduledTimes.map((schedule, scheduleIndex) => (
                      <div key={scheduleIndex} className="border border-secondary-200 rounded-xl p-4 bg-white">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-bold text-crust text-lg">Schedule #{scheduleIndex + 1}</h3>
                          <div className="text-sm px-3 py-1 bg-secondary-100 rounded-full">
                            {(schedule.meals || []).length} meal(s) selected
                          </div>
                        </div>
                        
                        {/* Schedule details summary */}
                        <div className="p-3 bg-cheese/10 rounded-lg border border-cheese/20 mb-4">
                          <p className="text-sm text-olive">
                            {schedule.scheduledFor ? (
                              schedule.repeatType === 'none' ? (
                                <>One-time delivery on {new Date(schedule.scheduledFor).toLocaleDateString()} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                              ) : schedule.repeatType === 'daily' ? (
                                schedule.endDate ? (
                                  <>Daily delivery at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} from {new Date(schedule.scheduledFor).toLocaleDateString()} to {new Date(schedule.endDate).toLocaleDateString()}</>
                                ) : (
                                  <>Daily delivery at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} starting {new Date(schedule.scheduledFor).toLocaleDateString()} until canceled</>
                                )
                              ) : (schedule.repeatDays || []).length > 0 ? (
                                schedule.endDate ? (
                                  <>Weekly delivery on {schedule.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} from {new Date(schedule.scheduledFor).toLocaleDateString()} to {new Date(schedule.endDate).toLocaleDateString()}</>
                                ) : (
                                  <>Weekly delivery on {schedule.repeatDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')} at {new Date(schedule.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} starting {new Date(schedule.scheduledFor).toLocaleDateString()} until canceled</>
                                )
                              ) : (
                                <>Please select days for your weekly delivery</>
                              )
                            ) : (
                              <>Please select a start date and time</>
                            )}
                          </p>
                        </div>
                        
                        {/* Meal selection table for this schedule */}
                        <div className="border border-secondary-200 rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-secondary-50">
                              <tr>
                                <th className="text-left py-2 px-3 font-bold text-crust">Meal</th>
                                <th className="text-right py-2 px-3 font-bold text-crust">Price</th>
                                <th className="text-center py-2 px-3 font-bold text-crust">Qty</th>
                                <th className="text-right py-2 px-3 font-bold text-crust">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {meals.map((meal) => {
                                const selectedMeal = (schedule.meals || []).find(m => m.meal === meal._id);
                                const isSelected = !!selectedMeal;
                                
                                return (
                                  <tr 
                                    key={meal._id}
                                    className={`border-b border-secondary-100 hover:bg-secondary-50 transition-colors ${
                                      isSelected ? 'bg-cheese/10' : ''
                                    }`}
                                  >
                                    <td className="py-2 px-3">
                                      <div>
                                        <h4 className="font-medium text-crust">{meal.name}</h4>
                                        <p className="text-olive-light text-xs line-clamp-1">{meal.description}</p>
                                      </div>
                                    </td>
                                    <td className="py-2 px-3 text-right font-bold text-pepperoni text-xs">
                                      ₦{meal.price}
                                    </td>
                                    <td className="py-2 px-3">
                                      {isSelected ? (
                                        <div className="flex items-center justify-center gap-1">
                                          <button
                                            onClick={() => {
                                              // Update quantity for this meal in this schedule
                                              const updatedMeals = (schedule.meals || []).map(m => 
                                                m.meal === meal._id ? {...m, quantity: Math.max(1, m.quantity - 1)} : m
                                              );
                                              updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                            }}
                                            className="w-6 h-6 rounded-full bg-secondary-200 flex items-center justify-center font-bold text-olive-light hover:bg-secondary-300 text-xs"
                                          >
                                            -
                                          </button>
                                          <span className="font-bold text-crust min-w-[1.5rem] text-center text-xs">
                                            {selectedMeal.quantity}
                                          </span>
                                          <button
                                            onClick={() => {
                                              // Update quantity for this meal in this schedule
                                              const updatedMeals = (schedule.meals || []).map(m => 
                                                m.meal === meal._id ? {...m, quantity: m.quantity + 1} : m
                                              );
                                              updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                            }}
                                            className="w-6 h-6 rounded-full bg-cheese flex items-center justify-center font-bold text-crust hover:bg-cheese/80 text-xs"
                                          >
                                            +
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="text-center text-gray-400 text-xs">—</div>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      {isSelected ? (
                                        <button
                                          onClick={() => {
                                            // Remove this meal from this schedule
                                            const updatedMeals = (schedule.meals || []).filter(m => m.meal !== meal._id);
                                            updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                          }}
                                          className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors"
                                        >
                                          Remove
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            // Add this meal to this schedule
                                            const updatedMeals = [...(schedule.meals || []), { meal: meal._id, quantity: 1 }];
                                            updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                          }}
                                          className="text-xs px-2 py-1 bg-pepperoni text-white rounded-lg font-medium hover:bg-primary-800 transition-colors"
                                        >
                                          Add
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-8 bg-secondary-50 border-2 border-dashed border-secondary-200 rounded-xl">
                      <p className="text-olive-light mb-4 font-medium">No schedules available to select meals for.</p>
                      <button
                        onClick={() => setStep(1)}
                        className="bg-cheese text-crust px-4 py-2 rounded-xl font-bold text-sm hover:bg-cheese/90 transition-all"
                      >
                        Go Back to Add Schedules
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Display selected meal count for regular scheduling */}
              {!useMultipleSchedules && (
                <div className="mt-4 p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-crust">Selected Meals:</span>
                    <span className="font-bold text-pepperoni">{orderData.meals?.length || 0}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-all"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    const errors = validateOrderData();
                    if (errors.length > 0) {
                      showErrorNotification('Please fix the following errors:', { details: errors.join(', ') });
                      return;
                    }
                    setStep(4);
                    generatePreview();
                  }}
                  className="px-8 py-3 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Review Order
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-crust mb-2">Review Your Order</h2>
                <p className="text-olive-light">Confirm your scheduled order details and pricing</p>
              </div>

              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
                  <span className="ml-3 text-olive-light">Calculating costs...</span>
                </div>
              ) : preview ? (
                <div className="space-y-6">
                  {/* Schedule Summary */}
                  <div className="bg-secondary-50 rounded-xl p-6">
                    <h3 className="font-bold text-crust mb-4 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5" />
                      Schedule Details
                    </h3>
                    
                    {useMultipleSchedules ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-olive-light">You have configured {preview.schedules.length} schedule{preview.schedules.length !== 1 ? 's' : ''}:</p>
                          <span className="text-xs px-2 py-1 bg-secondary-100 rounded-full">
                            {preview.summary.totalExecutions} total execution{preview.summary.totalExecutions !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        {preview.schedules.map((schedule, index) => (
                          <div key={index} className="border border-secondary-200 rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-bold text-crust">Schedule #{index + 1}</h4>
                              <span className="text-xs px-2 py-1 bg-secondary-100 rounded-full">
                                {schedule.executionCount} execution{schedule.executionCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-olive-light">First Execution:</span>
                                <p className="font-semibold text-crust">
                                  {new Date(schedule.scheduledFor).toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <span className="text-olive-light">Repeat Pattern:</span>
                                <p className="font-semibold text-crust">
                                  {schedule.repeatType === 'none' ? 'One-time order' :
                                    schedule.repeatType === 'daily' ? 'Daily' :
                                      `Weekly (${orderData.scheduledTimes[index]?.repeatDays?.join(', ') || 'days pending'})`}
                                </p>
                              </div>
                              {orderData.scheduledTimes[index]?.endDate && (
                                <div>
                                  <span className="text-olive-light">End Date:</span>
                                  <p className="font-semibold text-crust">
                                    {new Date(orderData.scheduledTimes[index].endDate).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                              <div>
                                <span className="text-olive-light">Meals Per Execution:</span>
                                <p className="font-semibold text-crust">{schedule.meals?.length || 0} meal type{(schedule.meals?.length || 0) !== 1 ? 's' : ''}</p>
                              </div>
                              <div className="md:col-span-2 mt-1 pt-1 border-t border-secondary-100">
                                <span className="text-olive-light">Food Amount Per Execution:</span>
                                <p className="font-semibold text-crust">₦{schedule.foodAmountPerExecution.toLocaleString()}</p>
                              </div>
                              <div className="md:col-span-2">
                                <span className="text-olive-light">Total Food Amount for All Executions:</span>
                                <p className="font-semibold text-crust">₦{schedule.totalFoodForSchedule.toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-olive-light">First Execution:</span>
                          <p className="font-semibold text-crust">
                            {preview.schedules && preview.schedules[0] ? 
                              new Date(preview.schedules[0].scheduledFor).toLocaleString() : 
                              "Loading..."}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-olive-light">Repeat Pattern:</span>
                          <p className="font-semibold text-crust">
                            {orderData.repeatType === 'none' ? 'One-time order' :
                              orderData.repeatType === 'daily' ? 'Daily' :
                                `Weekly (${orderData.repeatDays?.join(', ')})`}
                          </p>
                        </div>
                        {orderData.endDate && (
                          <div>
                            <span className="text-sm text-olive-light">End Date:</span>
                            <p className="font-semibold text-crust">
                              {new Date(orderData.endDate).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        <div>
                          <span className="text-sm text-olive-light">Total Executions:</span>
                          <p className="font-semibold text-crust">{preview.summary?.totalExecutions || 0}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-olive-light">Food Amount Per Execution:</span>
                          <p className="font-semibold text-crust">₦{preview.schedules[0]?.foodAmountPerExecution.toLocaleString()}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-olive-light">Total Food Amount:</span>
                          <p className="font-semibold text-crust">₦{preview.summary.totalFoodAmount.toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Meals Summary */}
                  <div className="bg-secondary-50 rounded-xl p-6">
                    <h3 className="font-bold text-crust mb-4 flex items-center gap-2">
                      <ShoppingBagIcon className="w-5 h-5" />
                      Selected Meals
                    </h3>
                    <div className="space-y-3">
                      {useMultipleSchedules ? (
                        // For multi-schedule, show meals for each schedule with editing options
                        preview.schedules?.map((schedule, scheduleIndex) => (
                          <div key={scheduleIndex} className="mb-6 border border-secondary-200 rounded-lg p-4 bg-white">
                            <h4 className="font-bold text-crust mb-2">Schedule #{scheduleIndex + 1}</h4>
                            
                            {/* Meal selection UI for this schedule */}
                            <div className="mb-3">
                              <div className="flex justify-between items-center mb-2">
                                <h5 className="font-medium text-olive-light">Meals for this Schedule</h5>
                                <span className="text-xs px-2 py-1 bg-secondary-100 rounded-full">
                                  {(orderData.scheduledTimes[scheduleIndex]?.meals || []).length} meal(s) selected
                                </span>
                              </div>
                              
                              {meals.length > 0 ? (
                                <div className="border border-secondary-200 rounded-lg overflow-hidden">
                                  <table className="w-full text-sm">
                                    <thead className="bg-secondary-50">
                                      <tr>
                                        <th className="text-left py-2 px-3 font-semibold text-crust">Meal</th>
                                        <th className="text-right py-2 px-3 font-semibold text-crust w-24">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {meals.map((meal) => {
                                        const selectedMeal = (orderData.scheduledTimes[scheduleIndex]?.meals || []).find(m => m.meal === meal._id);
                                        const isSelected = !!selectedMeal;
                                        
                                        return (
                                          <tr 
                                            key={meal._id}
                                            className={`border-b border-secondary-100 hover:bg-secondary-50 transition-colors ${
                                              isSelected ? 'bg-cheese/10' : ''
                                            }`}
                                          >
                                            <td className="py-2 px-3">
                                              <div className="flex items-center">
                                                {isSelected && (
                                                  <span className="text-xs bg-cheese text-white px-2 py-0.5 rounded-full mr-2">
                                                    {selectedMeal.quantity}x
                                                  </span>
                                                )}
                                                <div>
                                                  <p className={`font-medium ${isSelected ? 'text-crust' : 'text-olive-light'}`}>
                                                    {meal.name}
                                                  </p>
                                                </div>
                                              </div>
                                            </td>
                                            <td className="py-2 px-3 text-right">
                                              {isSelected ? (
                                                <div className="flex items-center justify-end gap-1">
                                                  <button
                                                    onClick={() => {
                                                      // Update quantity for this meal in this schedule
                                                      const updatedMeals = (orderData.scheduledTimes[scheduleIndex]?.meals || []).map(m => 
                                                        m.meal === meal._id ? {...m, quantity: Math.max(1, m.quantity - 1)} : m
                                                      );
                                                      updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                                    }}
                                                    className="w-6 h-6 rounded-full bg-secondary-200 flex items-center justify-center font-bold text-olive-light hover:bg-secondary-300 text-xs"
                                                  >
                                                    -
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      // Update quantity for this meal in this schedule
                                                      const updatedMeals = (orderData.scheduledTimes[scheduleIndex]?.meals || []).map(m => 
                                                        m.meal === meal._id ? {...m, quantity: m.quantity + 1} : m
                                                      );
                                                      updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                                    }}
                                                    className="w-6 h-6 rounded-full bg-cheese flex items-center justify-center font-bold text-crust hover:bg-cheese/80 text-xs"
                                                  >
                                                    +
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      // Remove this meal from this schedule
                                                      const updatedMeals = (orderData.scheduledTimes[scheduleIndex]?.meals || []).filter(m => m.meal !== meal._id);
                                                      updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                                    }}
                                                    className="ml-1 text-xs text-red-500 hover:text-red-700"
                                                  >
                                                    Remove
                                                  </button>
                                                </div>
                                              ) : (
                                                <button
                                                  onClick={() => {
                                                    // Add this meal to this schedule
                                                    const updatedMeals = [...(orderData.scheduledTimes[scheduleIndex]?.meals || []), { meal: meal._id, quantity: 1 }];
                                                    updateSchedule(scheduleIndex, 'meals', updatedMeals);
                                                  }}
                                                  className="text-xs px-2 py-1 bg-pepperoni text-white rounded-lg hover:bg-primary-800 transition-colors"
                                                >
                                                  Add
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center p-4 bg-secondary-50 border border-dashed border-secondary-200 rounded-lg">
                                  <p className="text-olive-light text-sm">No meals available. Please go back and select a restaurant with available meals.</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Display currently selected meals from preview */}
                            <div className="mt-3 pt-3 border-t border-secondary-200">
                              <div className="flex justify-between items-center mb-2">
                                <p className="text-sm font-medium text-olive">Selected meals for this schedule:</p>
                                <span className="text-xs px-2 py-1 bg-cheese/20 text-pepperoni rounded-full">
                                  ₦{schedule.foodAmountPerExecution.toLocaleString()} per execution
                                </span>
                              </div>
                              <div className="border border-secondary-100 rounded-lg overflow-hidden bg-white">
                                <table className="w-full text-sm">
                                  <thead className="bg-secondary-50">
                                    <tr>
                                      <th className="text-left py-2 px-3 font-medium text-crust">Meal</th>
                                      <th className="text-center py-2 px-3 font-medium text-crust">Qty</th>
                                      <th className="text-right py-2 px-3 font-medium text-crust">Price</th>
                                      <th className="text-right py-2 px-3 font-medium text-crust">Subtotal</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {schedule.meals.map((meal, idx) => (
                                      <tr key={idx} className="border-b border-secondary-50 last:border-b-0">
                                        <td className="py-2 px-3">{meal.name}</td>
                                        <td className="py-2 px-3 text-center">{meal.quantity}</td>
                                        <td className="py-2 px-3 text-right">₦{meal.price.toLocaleString()}</td>
                                        <td className="py-2 px-3 text-right font-medium text-pepperoni">₦{meal.subtotal.toLocaleString()}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-secondary-50 border-t border-secondary-100">
                                    <tr>
                                      <td colSpan="3" className="py-2 px-3 text-right font-medium">Total:</td>
                                      <td className="py-2 px-3 text-right font-bold text-pepperoni">₦{schedule.foodAmountPerExecution.toLocaleString()}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        // For single schedule
                        <div className="border border-secondary-100 rounded-lg overflow-hidden bg-white">
                          <table className="w-full">
                            <thead className="bg-secondary-50">
                              <tr>
                                <th className="text-left py-2 px-3 font-medium text-crust">Meal</th>
                                <th className="text-center py-2 px-3 font-medium text-crust">Qty</th>
                                <th className="text-right py-2 px-3 font-medium text-crust">Price</th>
                                <th className="text-right py-2 px-3 font-medium text-crust">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.schedules?.[0]?.meals.map((meal, index) => (
                                <tr key={index} className="border-b border-secondary-50 last:border-b-0">
                                  <td className="py-2 px-3">{meal.name}</td>
                                  <td className="py-2 px-3 text-center">{meal.quantity}</td>
                                  <td className="py-2 px-3 text-right">₦{meal.price.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right font-medium text-pepperoni">₦{meal.subtotal.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-secondary-50 border-t border-secondary-100">
                              <tr>
                                <td colSpan="3" className="py-2 px-3 text-right font-medium">Total:</td>
                                <td className="py-2 px-3 text-right font-bold text-pepperoni">
                                  ₦{preview.schedules?.[0]?.foodAmountPerExecution.toLocaleString()}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="bg-gradient-to-r from-cheese/10 to-secondary-100 rounded-xl p-6">
                    <h3 className="font-bold text-crust mb-4 flex items-center gap-2">
                      <CheckCircleIcon className="w-5 h-5" />
                      Cost Breakdown
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-olive-light">Food Amount (All Schedules):</span>
                        <span className="font-semibold">₦{preview.summary.totalFoodAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-olive-light">Delivery Fee (₦{preview.summary.deliveryFeePerExecution} × {preview.summary.totalExecutions}):</span>
                        <span className="font-semibold">₦{preview.summary.totalDeliveryFee.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-olive-light">Scheduling Fee (₦{preview.summary.schedulingFeePerExecution} × {preview.summary.totalExecutions}):</span>
                        <span className="font-semibold">₦{preview.summary.totalSchedulingFee.toLocaleString()}</span>
                      </div>
                      
                      {useMultipleSchedules && preview.summary.perScheduleTotals && (
                        <div className="mt-4 pt-4 border-t border-secondary-300">
                          <h4 className="font-medium text-crust mb-3">Per Schedule Breakdown:</h4>
                          <div className="space-y-3">
                            {preview.summary.perScheduleTotals.map((schedule, index) => (
                              <div key={index} className="p-3 bg-white rounded-lg border border-secondary-200">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium text-crust">Schedule #{index + 1}</span>
                                  <span className="text-xs bg-secondary-100 rounded-full px-2 py-0.5">
                                    {schedule.executionCount} execution{schedule.executionCount !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <div className="text-sm space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-olive-light">Food Amount (per execution):</span>
                                    <span>₦{schedule.foodAmountPerExecution.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-olive-light">Total Food Cost:</span>
                                    <span>₦{schedule.totalFoodCost.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-olive-light">Delivery Fees:</span>
                                    <span>₦{schedule.deliveryTotal.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-olive-light">Scheduling Fees:</span>
                                    <span>₦{schedule.schedulingTotal.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between font-medium border-t border-secondary-100 pt-1 mt-1">
                                    <span>Schedule Total:</span>
                                    <span className="text-pepperoni">₦{schedule.scheduleTotal.toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="border-t border-secondary-300 pt-3 flex justify-between">
                        <span className="text-olive-light">Average Cost Per Execution:</span>
                        <span className="font-bold text-lg">₦{Math.round(preview.summary.grandTotal / preview.summary.totalExecutions).toLocaleString()}</span>
                      </div>
                      
                      <div className="bg-pepperoni/10 rounded-lg p-3 flex justify-between items-center">
                        <span className="font-bold text-crust">Total Amount ({preview.summary.totalExecutions} executions):</span>
                        <span className="font-bold text-2xl text-pepperoni">₦{preview.summary.grandTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Information */}
                  <div className="bg-secondary-50 rounded-xl p-6">
                    <h3 className="font-bold text-crust mb-4">Delivery Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-olive-light">Recipient:</span>
                        <p className="font-semibold text-crust">{orderData.recipient.name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Special Instructions */}
                  <div>
                    <label className="block text-sm font-bold text-crust mb-2">Special Instructions (Optional)</label>
                    <textarea
                      value={orderData.note}
                      onChange={(e) => updateOrderData('note', e.target.value)}
                      placeholder="Any special instructions for your order..."
                      rows={3}
                      className="w-full p-4 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans resize-none"
                    />
                  </div>

                  {/* Warnings */}
                  {preview.warnings && preview.warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                        <h4 className="font-bold text-yellow-800">Important Notes</h4>
                      </div>
                      <ul className="list-disc list-inside space-y-1">
                        {preview.warnings.map((warning, index) => (
                          <li key={index} className="text-yellow-700 text-sm">{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="flex-1 bg-gray-200 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-300 transition-all"
                    >
                      Back to Meals
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                    >
                      {loading ? 'Creating Order...' : `Pay ₦${preview.summary.grandTotal.toLocaleString()} & Create Order`}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-crust mb-2">Unable to Generate Preview</h3>
                  <p className="text-olive-light mb-2">Please check your order details and try again.</p>
                  
                  {/* Added validation summary */}
                  <div className="mb-6 max-w-lg mx-auto bg-red-50 p-4 rounded-lg border border-red-100 text-left">
                    <h4 className="font-bold text-red-700 mb-2">Please fix the following issues:</h4>
                    <ul className="list-disc list-inside space-y-1 text-red-600 text-sm">
                      {validateOrderData().map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                    {useMultipleSchedules && (
                      <p className="mt-3 text-xs text-red-700 border-t border-red-200 pt-2">
                        <strong>How to fix this:</strong> 
                        <ol className="list-decimal list-inside mt-1 space-y-1">
                          <li>Make sure all schedules have valid times (at least 30 minutes in the future)</li>
                          <li>In the current Review step, select at least one meal for each schedule</li>
                        </ol>
                      </p>
                    )}
                  </div>
                  
                  <button
                    onClick={generatePreview}
                    className="bg-pepperoni text-accentwhite px-6 py-3 rounded-xl font-bold hover:bg-primary-800 transition-colors"
                  >
                    Retry Preview
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CreateScheduledOrder;